// src/components/VehicleDocumentsModal.jsx
import { useEffect, useState } from 'react';
import { 
  Modal, Text, Group, Badge, Paper, ThemeIcon, Divider, 
  Stack, Button, Select, FileInput, Table, ActionIcon, LoadingOverlay, Flex, ScrollArea
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { 
  IconUpload, IconFileText, IconDownload, IconTrash, IconFileCertificate, IconAlertTriangle 
} from '@tabler/icons-react';
import api from '../../api/axiosConfig';
import dayjs from 'dayjs';

export default function VehicleDocumentsModal({ opened, close, vehicle }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Form States
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState('Ruhsat');

  // Silme Onay Modalı için States
  const [docToDelete, setDocToDelete] = useState(null);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const documentTypes = [
    { value: 'Ruhsat', label: 'Ruhsat' },
    { value: 'Trafik Sigortası', label: 'Trafik Sigortası' },
    { value: 'Kasko Poliçesi', label: 'Kasko Poliçesi' },
    { value: 'Muayene Raporu', label: 'Muayene Raporu' },
    { value: 'Egzoz Emisyon', label: 'Egzoz Emisyon' },
    { value: 'Diğer', label: 'Diğer' },
  ];

  useEffect(() => {
    if (vehicle && opened) {
      fetchDocuments();
      setFile(null); 
    }
  }, [vehicle, opened]);

  const fetchDocuments = async () => {
    if (!vehicle?.id) return;
    setLoading(true);
    try {
      const response = await api.get(`/vehicles/documents/vehicle/${vehicle.id}`);
      setDocuments(response.data);
    } catch (error) {
      console.error("Belgeler çekilemedi:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !docType) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", docType);
      formData.append("vehicle_id", vehicle.id);

      await api.post('/vehicles/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFile(null);
      fetchDocuments(); 
    } catch (error) {
      console.error("Yükleme hatası:", error);
      alert("Dosya yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (docId) => {
    setDocToDelete(docId);
    openDeleteModal();
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;
    
    setLoading(true);
    try {
       await api.delete(`/vehicles/documents/${docToDelete}`);
       setDocuments(documents.filter(d => d.id !== docToDelete));
       
       closeDeleteModal();
       setDocToDelete(null);
    } catch (error) {
       console.error("Silme hatası:", error);
    } finally {
       setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/vehicles/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name); 
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("İndirme hatası:", error);
      alert("Dosya indirilemedi.");
    }
  };

  if (!vehicle) return null;

  return (
    <>
      <Modal opened={opened} onClose={close} title="Araç Belgeleri" centered size="xl" overlayProps={{ blur: 3 }}>
        <LoadingOverlay visible={loading} overlayProps={{ blur: 1 }} />
        
        {/* Modal Başlığı - Araç Bilgisi */}
        <Paper withBorder p="md" mb="lg" bg="var(--mantine-color-gray-0)" radius="md">
          <Group justify="space-between" align="center">
            <Group>
              <ThemeIcon size="xl" radius="md" variant="gradient" gradient={{ from: 'violet', to: 'grape' }}>
                  <IconFileCertificate size={24} />
              </ThemeIcon>
              <div>
                  <Text fw={800} size="xl">{vehicle.plate}</Text>
                  <Text size="sm" c="dimmed" fw={500}>{vehicle.brand} - {vehicle.commercial_type || 'Belirtilmemiş'}</Text>
              </div>
            </Group>
          </Group>
        </Paper>

        {/* Belge Yükleme Alanı */}
        <Paper withBorder p="md" radius="md" mb="xl">
          <Text fw={700} mb="sm" c="dark.7">Yeni Belge Yükle</Text>
          <Flex gap="md" align="flex-end" wrap="nowrap">
            <Select
              label="Belge Türü"
              data={documentTypes}
              value={docType}
              onChange={setDocType}
              required
              style={{ flex: 1 }}
            />
            <FileInput
              label="Dosya Seç"
              placeholder="PDF, JPG veya PNG seçin"
              value={file}
              onChange={setFile}
              accept="image/png,image/jpeg,application/pdf"
              leftSection={<IconUpload size={16} />}
              required
              style={{ flex: 2 }}
            />
            <Button onClick={handleUpload} disabled={!file} color="violet" leftSection={<IconUpload size={16}/>}>
              Yükle
            </Button>
          </Flex>
        </Paper>

        <Divider label={<Group gap={5}><IconFileText size={14}/><span>Yüklü Belgeler</span></Group>} labelPosition="left" mb="md" />

        {/* Belgeler Tablosu */}
        {documents.length > 0 ? (
          <ScrollArea h={350} offsetScrollbars type="auto">
            <Table stickyHeader striped highlightOnHover verticalSpacing="sm">
              <Table.Thead bg="gray.0">
                <Table.Tr>
                  <Table.Th style={{ width: '150px' }}>Belge Türü</Table.Th>
                  <Table.Th>Dosya Adı</Table.Th>
                  <Table.Th style={{ width: '130px' }}>Yüklenme Tarihi</Table.Th>
                  <Table.Th style={{ width: '100px', textAlign: 'right' }}>İşlem</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {documents.map((doc) => (
                  <Table.Tr key={doc.id}>
                    <Table.Td>
                        <Badge dot={true} color="violet" size="md">{doc.document_type}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={8} wrap="nowrap">
                        <ThemeIcon variant="light" color="gray" size="sm" radius="xl">
                            <IconFileText size={12} />
                        </ThemeIcon>
                        <Text size="sm" fw={500} style={{ 
                            wordBreak: 'break-all', 
                            display: '-webkit-box', 
                            WebkitLineClamp: 2, 
                            WebkitBoxOrient: 'vertical', 
                            overflow: 'hidden' 
                        }}>
                            {doc.file_name}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                        <Text size="sm" fw={500}>{dayjs(doc.upload_date).format('DD.MM.YYYY')}</Text>
                        <Text size="xs" c="dimmed">{dayjs(doc.upload_date).format('HH:mm')}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <ActionIcon variant="light" color="blue" size="md" title="İndir/Görüntüle" onClick={() => handleDownload(doc)}>
                          <IconDownload size={18} />
                        </ActionIcon>
                        <ActionIcon variant="light" color="red" size="md" title="Sil" onClick={() => handleDeleteClick(doc.id)}>
                          <IconTrash size={18} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        ) : (
          <Stack align="center" justify="center" h={200} gap="sm">
            <ThemeIcon size={64} radius="100%" variant="light" color="gray">
                <IconFileText size={32} />
            </ThemeIcon>
            <Text c="dimmed" fw={500}>Bu araca ait henüz bir belge yüklenmemiş.</Text>
          </Stack>
        )}
      </Modal>

      {/* SİLME ONAY MODALI */}
      <Modal 
        opened={deleteModalOpened} 
        onClose={closeDeleteModal} 
        title={
          <Group gap="sm">
            <ThemeIcon color="red" variant="light" radius="xl" size="md">
              <IconAlertTriangle size={18} />
            </ThemeIcon>
            <Text fw={700} size="lg">Belge Silme Onayı</Text>
          </Group>
        } 
        centered
        zIndex={300}
        overlayProps={{ blur: 3 }}
      >
        <Text size="sm" mb="xl">
          Bu belgeyi sistemden kalıcı olarak silmek istediğinize emin misiniz?
          <br /><br />
          <Text span c="red" size="sm" fw={600}>Dikkat: Dosya sunucudan tamamen silinecektir ve bu işlem geri alınamaz.</Text>
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={closeDeleteModal}>Vazgeç</Button>
          <Button color="red" onClick={confirmDelete} leftSection={<IconTrash size={16}/>}>Evet, Sil</Button>
        </Group>
      </Modal>
    </>
  );
}